import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { useCart } from "../contexts/CartContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_garnacha-nordic/artifacts/548npw6z_Sin%20t%C3%ADtulo%20%284500%20x%205100%20px%29.svg";

const Navbar = () => {
  const { t } = useLang();
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 10);
    h();
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/products", label: t.nav.products },
    { to: "/our-history", label: t.nav.about },
    { to: "/b2b", label: t.nav.b2b },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#FFF8F0]/95 backdrop-blur-xl border-b border-border shadow-[0_1px_24px_-12px_rgba(0,0,0,0.25)]" : "bg-transparent"
      }`}
    >
      <div className={`container-pad flex items-center justify-between transition-all duration-300 ${scrolled ? "h-20" : "h-28"}`}>
        <Link to="/" data-testid="brand-link" className="flex items-center gap-1 group">
          <img
            src={LOGO_URL}
            alt="GARNACHA Mexican Street Food"
            className={`w-auto object-contain transition-all duration-300 ${
              scrolled ? "h-14 sm:h-16" : "h-20 sm:h-24 lg:h-28"
            }`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide transition-colors relative ${
                  isActive ? "text-[#F97316] after:absolute after:-bottom-1.5 after:left-0 after:right-0 after:h-px after:bg-[#F97316]" : "text-foreground hover:text-[#F97316]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="cart-toggle-btn"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 text-sm font-medium hover:text-[#F97316] transition-colors"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-[#F97316] text-[#FFF8F0] text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
                {count}
              </span>
            )}
          </button>

          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#FFF8F0] border-t border-border">
          <div className="container-pad py-6 flex flex-col gap-5">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                onClick={() => setMobileOpen(false)}
                className="text-lg display-serif"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
